import type { ChangeEvent, Dispatch, SetStateAction } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { currencyMaskBRL } from "helpers/utils";
import { formatarMoedaBRL } from "../helpers/financeiro-formatters";
import { saldoDisponivelTransferencia } from "../helpers/contas-financeiras-saldo-efi";
import {
  CONTAS_FIN_ACCENT,
  fieldSx,
  modalHeaderSx,
  modalPaperSx,
  saveButtonSx,
} from "../../components/contas-financeiras.theme";
import type {
  ContaFinanceiraGerencialApi,
  TransferenciaContaFinanceiraFormPayload,
} from "../types/contas-financeiras.types";

export type TransferenciaContaFinanceiraDialogState = {
  open: boolean;
  contaOrigem: ContaFinanceiraGerencialApi | null;
  /** Atalho de repasse manual a partir da Efí (destino escolhido no modal). */
  modoRepasse?: boolean;
};

type Props = {
  state: TransferenciaContaFinanceiraDialogState;
  contasDestino: ContaFinanceiraGerencialApi[];
  form: TransferenciaContaFinanceiraFormPayload;
  setForm: Dispatch<SetStateAction<TransferenciaContaFinanceiraFormPayload>>;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
};

export function formPadraoTransferenciaConta(
  partial?: Partial<TransferenciaContaFinanceiraFormPayload>,
): TransferenciaContaFinanceiraFormPayload {
  return {
    contaDestinoId: "",
    valor: "",
    observacao: "",
    ...partial,
  };
}

export function observacaoRepasseManual(
  nomeDestino: string,
  nomeOrigem = "Efí",
): string {
  return `Repasse manual ${nomeOrigem} → ${nomeDestino}`;
}

export default function TransferenciaContaFinanceiraDialog({
  state,
  contasDestino,
  form,
  setForm,
  loading,
  error,
  onClose,
  onConfirm,
}: Props) {
  const origem = state.contaOrigem;
  const saldoOrigem = origem ? saldoDisponivelTransferencia(origem) : 0;
  const repasse = Boolean(state.modoRepasse);

  const patch =
    (field: keyof TransferenciaContaFinanceiraFormPayload) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const alterarContaDestino = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const destinoId = e.target.value;
    const contaDestino = contasDestino.find((c) => c.id === destinoId);

    setForm((prev) => {
      const next = { ...prev, contaDestinoId: destinoId };
      if (repasse && contaDestino) {
        next.observacao = observacaoRepasseManual(contaDestino.nome);
      }
      return next;
    });
  };

  const usarSaldoTotal = () => {
    if (!origem || saldoOrigem <= 0) return;
    setForm((prev) => ({
      ...prev,
      valor: saldoOrigem.toFixed(2).replace(".", ","),
    }));
  };

  return (
    <Dialog
      open={state.open}
      onClose={loading ? undefined : onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: modalPaperSx }}
      aria-labelledby="transferencia-conta-financeira-title"
    >
      <Box sx={modalHeaderSx}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box minWidth={0}>
            <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1.2 }}>
              {repasse ? "Repasse" : "Transferência"}
            </Typography>
            <Typography id="transferencia-conta-financeira-title" variant="h5" fontWeight="bold">
              {repasse ? "Registrar repasse" : "Transferência entre contas"}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.85 }}>
              {repasse
                ? "Registro contábil após sacar ou transferir no painel Efí."
                : "Transfira saldo entre contas ativas. A movimentação aparece no extrato."}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "inherit" }} aria-label="Fechar" disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>
      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SwapHorizOutlinedIcon sx={{ color: CONTAS_FIN_ACCENT }} />
            <MDTypography variant="button" fontWeight="bold">
              Dados da transferência
            </MDTypography>
          </Stack>
          {repasse ? (
            <Alert severity="warning">
              Este registro não transfere dinheiro na Efí. Confirme que o valor
              já foi sacado ou transferido no painel Efí.
            </Alert>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Box
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              border: 1,
              borderColor: "divider",
              bgcolor: "action.hover",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Conta de origem
            </Typography>
            <Typography variant="subtitle2" fontWeight={700}>
              {origem?.nome ?? "—"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Saldo disponível:{" "}
              <Typography
                component="span"
                variant="body2"
                fontWeight={600}
                color="text.primary"
              >
                {formatarMoedaBRL(saldoOrigem)}
              </Typography>
            </Typography>
          </Box>

          <TextField
            select
            label="Conta de destino"
            value={form.contaDestinoId}
            onChange={alterarContaDestino}
            fullWidth
            required
            size="small"
            sx={fieldSx}
          >
            <MenuItem value="">
              <em>Selecione a conta de destino</em>
            </MenuItem>
            {contasDestino.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.nome} ({c.tipoLabel}) — {formatarMoedaBRL(c.saldoAtual)}
              </MenuItem>
            ))}
          </TextField>

          <Box>
            <TextField
              label="Valor a transferir (R$)"
              value={currencyMaskBRL(form.valor || "0")}
              onChange={patch("valor")}
              fullWidth
              required
              size="small"
              sx={fieldSx}
            />
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.75 }}>
              <MDButton
                variant="text"
                color="dark"
                size="small"
                onClick={usarSaldoTotal}
                disabled={!origem || saldoOrigem <= 0}
                sx={{ color: CONTAS_FIN_ACCENT }}
              >
                Usar saldo total
              </MDButton>
            </Stack>
          </Box>

          <TextField
            label="Observação (opcional)"
            value={form.observacao}
            onChange={patch("observacao")}
            fullWidth
            multiline
            minRows={2}
            size="small"
            placeholder="Ex.: Repasse manual do gateway para caixa físico"
            sx={fieldSx}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "grey.200" }}>
        <MDButton variant="outlined" color="dark" onClick={onClose} disabled={loading}>
          Cancelar
        </MDButton>
        <MDButton
          variant="gradient"
          color="dark"
          onClick={() => void onConfirm()}
          disabled={
            loading ||
            !origem ||
            !form.contaDestinoId ||
            !form.valor.trim()
          }
          sx={saveButtonSx}
        >
          {loading ? "Transferindo…" : repasse ? "Registrar repasse" : "Transferir"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
